import { useCallback, useEffect, useMemo, useState } from 'react';

import { deleteValues, getStorageValue, saveState } from 'utils';
import { IFilterSelection } from 'utils/memberFilters';

interface IPersistentFilterConfig {
  name: string;
  stateKey: string;
  scope?: number | string;
}

interface IPersistentFilterCategory extends IPersistentFilterConfig {
  filterSelections: IFilterSelection[];
  setFilterSelections: (filterSelections: IFilterSelection[]) => void;
}

type PersistentFilterValues = Record<string, IFilterSelection[]>;

interface IUsePersistentFiltersResult {
  filters: IPersistentFilterCategory[];
  filterSelections: IFilterSelection[];
  isActive: boolean;
  getFilterSelections: (name: string) => IFilterSelection[];
  setFilterSelections: (name: string, filterSelections: IFilterSelection[]) => void;
  clearFilterSelections: (name: string) => void;
}

const getScopedStateKey = (config: IPersistentFilterConfig): string =>
  config.scope === undefined ? config.stateKey : `${config.stateKey}:${config.scope}`;

const isFilterSelection = (value: unknown): value is IFilterSelection => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const selection = value as IFilterSelection;
  return (
    (typeof selection.id === 'string' || typeof selection.id === 'number') &&
    typeof selection.label === 'string'
  );
};

const getStoredSelections = (config: IPersistentFilterConfig): IFilterSelection[] => {
  const storedValue = getStorageValue(getScopedStateKey(config), []);
  return Array.isArray(storedValue) && storedValue.every(isFilterSelection)
    ? storedValue
    : [];
};

// Hydrates validated selections and owns their persistence, so every filter
// surface follows the same safe read/write/remove behavior.
const usePersistentFilters = (
  configs: IPersistentFilterConfig[],
): IUsePersistentFiltersResult => {
  const configKey = configs
    .map(config => `${config.name}:${getScopedStateKey(config)}`)
    .join('|');
  const [filterValues, setFilterValues] = useState<PersistentFilterValues>(() =>
    configs.reduce<PersistentFilterValues>(
      (acc, config) => ({ ...acc, [config.name]: getStoredSelections(config) }),
      {},
    ),
  );

  const setFilterSelections = useCallback(
    (name: string, filterSelections: IFilterSelection[]): void => {
      const config = configs.find(item => item.name === name);
      if (!config) {
        return;
      }

      const stateKey = getScopedStateKey(config);
      if (filterSelections.length) {
        saveState(stateKey, filterSelections);
      } else {
        deleteValues(stateKey);
      }

      setFilterValues(prevState => ({ ...prevState, [name]: filterSelections }));
    },
    [configs],
  );

  useEffect(() => {
    setFilterValues(
      configs.reduce<PersistentFilterValues>(
        (acc, config) => ({ ...acc, [config.name]: getStoredSelections(config) }),
        {},
      ),
    );
  }, [configKey]);

  const getFilterSelections = useCallback(
    (name: string): IFilterSelection[] => filterValues[name] ?? [],
    [filterValues],
  );

  const clearFilterSelections = useCallback(
    (name: string): void => setFilterSelections(name, []),
    [setFilterSelections],
  );

  const filters = useMemo<IPersistentFilterCategory[]>(
    () =>
      configs.map(config => ({
        ...config,
        filterSelections: getFilterSelections(config.name),
        setFilterSelections: filterSelections =>
          setFilterSelections(config.name, filterSelections),
      })),
    [configs, getFilterSelections, setFilterSelections],
  );

  const filterSelections = useMemo(
    () => filters.flatMap(filter => filter.filterSelections),
    [filters],
  );

  return {
    filters,
    filterSelections,
    isActive: Boolean(filterSelections.length),
    getFilterSelections,
    setFilterSelections,
    clearFilterSelections,
  };
};

export { usePersistentFilters };
export type { IUsePersistentFiltersResult };
